// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import { SyntheticEvent, useState, useContext } from 'react';
import styled from '@emotion/styled';

import HorizontalUserListItem, {
  ActiveBorder,
  Border,
  MenuAvatar,
  UserContainer,
} from 'presentations/views/components/molecules/HorizontalUserListItem';
import {
  fontSize,
  fontWeight,
  mediaMax,
  spacing,
  zIndex,
} from 'presentations/views/styles';
import { TeamUserListItem } from 'presentations/api/schema/team_user';
import {
  gray01,
  gray02,
  gray04,
  white,
  whiteFix,
} from 'presentations/views/styles/color';
import dynamic from 'next/dynamic';
import Icon from 'presentations/views/components/atoms/Icon';
import IconButton from 'presentations/views/components/atoms/IconButton';
import { ProjectListItem } from 'presentations/api/schema/project';
import { useProjectForm } from 'presentations/views/components/organisms/ProjectForm/hooks';
import {
  DashboardPortalContext,
  setErrorMessage,
  setProjects,
  setSuccessMessage,
} from 'presentations/reducers/DashboardPortal';
import { ViewerItem } from 'presentations/api/schema/user';
import { Role } from 'domains/models/team_user/role';
import { useHomeDisplayItemSettingForm } from '../HomeDisplayItemSettingForm/hooks';
import { useHomeVisibleSettingForm } from '../HomeVisibleSettingForm/hooks';
import { useFloatSettingMenu } from '../../molecules/FloatSettingMenu/hooks';
import { destroy } from 'infras/rest_client';
import { apiPath } from 'presentations/routes/path';
import { getErrorMessage } from 'presentations/views/helpers/error';

const ConfirmationDialog = dynamic(() =>
  import('presentations/views/components/molecules/ConfirmationDialog'),
);
const FlowSettingMenu = dynamic(() =>
  import('presentations/views/components/molecules/FloatSettingMenu'),
);
const TeamMemberSettingDialog = dynamic(() =>
  import(
    'presentations/views/components/organisms/features/team/TeamMemberSettingDialog'
  ),
);
const ProjectSettingDialog = dynamic(() =>
  import(
    'presentations/views/components/organisms/features/project/ProjectSettingDialog'
  ),
);
const ProjectForm = dynamic(() =>
  import('presentations/views/components/organisms/ProjectForm'),
);

export interface UserListFilter {
  userId?: string | null;
  projectId?: string | null;
}

interface Props {
  onChangeFilter?: (filters: UserListFilter) => void;
  teamUsers: TeamUserListItem[]; // localStorageからのmigration用にしばらくは必要
  userId: string | null;
  projectId?: string | null;
  viewer: ViewerItem;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);

  const [isConfirmLoading, setIsConfirmLoading] = useState(false);
  const { state, dispatch } = useContext(DashboardPortalContext);
  const [deleteProject, setDeleteProject] = useState<ProjectListItem | null>(
    null,
  );

  const homeDisplayItemSettingForm = useHomeDisplayItemSettingForm({
    viewer: props.viewer,
    teamUsers: props.teamUsers, // localStorageからのmigration用にしばらくは必要
    projects: state.projects,
    // openInviteMemberDialog: () => {
    //   dispatch(setIsOpenUserInviteForm(true));
    // },
    openProjectForm: () => projectForm.actions.setIsOpen(true),
  });

  const floatMenu = useFloatSettingMenu({
    onSelectMenu: (index) => {
      if (index === 0) {
        homeDisplayItemSettingForm.actions.setOpenInviteMember();
      } else if (index === 1) {
        homeDisplayItemSettingForm.actions.setOpenAddProject();
      }
    },
  });

  const onCreateProject = async (project: ProjectListItem) => {
    dispatch(setProjects([...state.projects, project]));
    homeDisplayItemSettingForm.actions.onProjectAdded(project);
  };

  const onDeleteProject = async () => {
    if (!deleteProject) {
      return;
    }

    setIsConfirmLoading(true);
    const result = await destroy(
      apiPath(
        `/v1/teams/${props.viewer.team!.uid}/projects/${
          deleteProject.uid
        }/delete`,
      ),
    );
    if (!result.isSuccess) {
      setIsConfirmLoading(false);
      dispatch(setErrorMessage(getErrorMessage(result)));
      return;
    }
    setDeleteProject(null);
    setIsConfirmLoading(false);
    dispatch(setSuccessMessage(t('Project deleted')));

    dispatch(
      setProjects(state.projects.filter((e) => e.id !== deleteProject.id)),
    );
    homeDisplayItemSettingForm.actions.onProjectDeleted(deleteProject);
  };

  const projectForm = useProjectForm({
    teamUid: props.viewer.team?.uid,
    onRequested: onCreateProject,
  });

  const displayProjects = homeDisplayItemSettingForm.state.selectedProjects;

  const homeVisibleSettingForm = useHomeVisibleSettingForm({
    viewer: props.viewer,
    projects: state.projects,
    selectedUsers: homeDisplayItemSettingForm.state.selectedUsers,
    onClickAddMemberButton:
      homeDisplayItemSettingForm.actions.setOpenInviteMember,
    onClickAddProjectButton:
      homeDisplayItemSettingForm.actions.setOpenAddProject,
  });

  function onClickAddProject() {
    projectForm.actions.setIsOpen(true);
    homeVisibleSettingForm.actions.setOpen(false);
  }

  function onChangeFilter(e: SyntheticEvent<HTMLDivElement>) {
    e.preventDefault();
    if (props.onChangeFilter) {
      const userId = e.currentTarget.dataset.id;
      const projectId = e.currentTarget.dataset.projectId;
      props.onChangeFilter({ userId, projectId });
    }
  }

  function onClickAll(e: SyntheticEvent<HTMLDivElement>) {
    e.preventDefault();
    if (props.onChangeFilter) {
      props.onChangeFilter({ userId: 'all' });
    }
  }

  const renderTeamUser = (displayTeamUsers: TeamUserListItem[]) => {
    return displayTeamUsers.map((teamUser) => (
      <HorizontalUserListItem
        key={teamUser.userId}
        userId={teamUser.userId}
        profileImage={teamUser.profileImage}
        onClick={onChangeFilter}
        isActive={props.userId === teamUser.userId}
        name={teamUser.name}
        color={teamUser.color}
      />
    ));
  };

  const renderMenu = () => {
    if (!floatMenu.state.anchor) return null;

    const options = [
      {
        icon: 'account-cog',
        label: t('menu1'),
      },
      {
        icon: 'folder-cog-outline',
        label: t('menu2'),
      },
    ];
    return (
      <FlowSettingMenu
        onCloseMenu={floatMenu.actions.onCloseMenu}
        onSelectMenu={floatMenu.actions.onSelectMenu}
        anchor={floatMenu.state.anchor}
        options={options}
      />
    );
  };

  return (
    <UserFilterContainer>
      {props.viewer.role <= Role.Member ? (
        <>
          <Container data-intercom-target="user-filter">
            <MainContent>
              {renderTeamUser(homeDisplayItemSettingForm.state.selectedUsers)}
              <ProjectList>
                {displayProjects.map((project) => (
                  <HorizontalUserListItem
                    key={project.uid}
                    projectId={project.uid}
                    icon={project.icon}
                    onClick={onChangeFilter}
                    isActive={props.projectId === project.uid}
                    name={project.name}
                  />
                ))}
              </ProjectList>
              <AllSelect>
                <UserContainer onClick={onClickAll}>
                  {props.userId === 'all' ? (
                    <ActiveBorder>
                      <MenuAvatar>
                        <AllIcon name="account-group" size={fontSize(24)} />
                      </MenuAvatar>
                    </ActiveBorder>
                  ) : (
                    <Border>
                      <MenuAvatar>
                        <AllIcon name="account-group" size={fontSize(24)} />
                      </MenuAvatar>
                    </Border>
                  )}
                  <Name>{t('All')}</Name>
                </UserContainer>
              </AllSelect>
            </MainContent>
            <AddContainer>
              <IconButton onClick={floatMenu.actions.onClickMenu}>
                <AddIcon name="settings" />
              </IconButton>
            </AddContainer>
            {renderMenu()}
          </Container>
          {homeDisplayItemSettingForm.state.isInviteMember && (
            <TeamMemberSettingDialog
              viewer={homeDisplayItemSettingForm.state.viewer}
              isOpen={homeDisplayItemSettingForm.state.isOpen}
              isLoading={homeDisplayItemSettingForm.state.isLoading}
              selectedUsers={homeDisplayItemSettingForm.state.selectedUsers}
              onClickAddButton={
                homeDisplayItemSettingForm.actions.onClickAddButton
              }
              onSubmit={homeDisplayItemSettingForm.actions.onSubmit}
              onClose={homeDisplayItemSettingForm.actions.onClose}
              onDeleteUser={homeDisplayItemSettingForm.actions.onDeleteUser}
              syncUsers={async (users) => {
                await homeDisplayItemSettingForm.actions.syncTeamUser(users);
              }}
            />
          )}
          {homeDisplayItemSettingForm.state.isAddProject && (
            <ProjectSettingDialog
              viewer={homeDisplayItemSettingForm.state.viewer}
              isOpen={homeDisplayItemSettingForm.state.isOpen}
              isLoading={homeDisplayItemSettingForm.state.isLoading}
              projects={homeDisplayItemSettingForm.state.projects}
              selectedProjects={
                homeDisplayItemSettingForm.state.selectedProjects
              }
              addButtonText={homeDisplayItemSettingForm.state.addButtonText}
              autoCompleteName={
                homeDisplayItemSettingForm.state.autoCompleteName
              }
              autoCompletePlaceholder={
                homeDisplayItemSettingForm.state.autoCompletePlaceholder
              }
              onDeleteProject={
                homeDisplayItemSettingForm.actions.onDeleteProject
              }
              setOpenAddProject={
                homeDisplayItemSettingForm.actions.setOpenAddProject
              }
              onClose={homeDisplayItemSettingForm.actions.onClose}
              onClickAddButton={
                homeDisplayItemSettingForm.actions.onClickAddButton
              }
              onClickDelete={(project: ProjectListItem) =>
                setDeleteProject(project)
              }
              syncProject={async (projects) => {
                await homeDisplayItemSettingForm.actions.syncProject(projects);
              }}
            />
          )}
          <ProjectForm
            isCreateMode={true}
            {...projectForm.state}
            {...projectForm.actions}
          />
          <ConfirmationDialog
            isLoading={isConfirmLoading}
            isOpen={deleteProject !== null}
            onSubmit={() => onDeleteProject()}
            onCancel={() => setDeleteProject(null)}
            title={t('Delete Project?')}
            submitLabel={t('Delete')}
          />
        </>
      ) : (
        <Container data-intercom-target="user-filter">
          {renderTeamUser(homeDisplayItemSettingForm.state.selectedUsers)}
        </Container>
      )}
    </UserFilterContainer>
  );
}

const UserFilterContainer = styled.div`
  padding: ${spacing(8)} ${spacing(24)};
  background-color: ${white};
  border-bottom: 1px solid ${gray04};
  width: calc(100vw - 72px);
  overflow: hidden;
  position: sticky;
  top: 0;
  left: 0;
  right: 0;
  z-index: ${zIndex(1100)};
  ${mediaMax.tablet} {
    width: 100%;
    padding: ${spacing(8)} ${spacing(12)};
  }
`;

const Container = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
const MainContent = styled.div`
  flex: 1;
  overflow-x: auto;
  display: flex;
  align-items: center;
  overflow-x: auto;
`;
const AddContainer = styled.div`
  padding-left: ${spacing(8)};
`;
const ProjectList = styled.div`
  border-left: 1px solid ${gray04};
  padding-left: ${spacing(16)};
  display: flex;
  align-items: center;
`;
const AllSelect = styled.div`
  border-left: 1px solid ${gray04};
  padding-left: ${spacing(16)};
  display: flex;
  align-items: center;
`;

const Name = styled.div`
  width: 54px;
  overflow: hidden;
  /* text-overflow: ellipsis; */
  white-space: nowrap;
  font-size: ${fontSize(10)};
  color: ${gray02};
  text-align: center;
  padding: 0 4px;
  letter-spacing: 0.04em;
  font-weight: ${fontWeight('bold')};
`;

const AllIcon = styled(Icon)`
  color: ${whiteFix};
`;
const AddIcon = styled(Icon)`
  color: ${gray01};
  width: 24px !important;
  height: 24px !important;
`;
